import { Heart, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useHeartRateTrends, HeartRateTrendPoint } from '@/hooks/use-health-trends';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';

interface HeartRateTrendsChartProps {
  clientId: string;
  days?: number;
  className?: string;
}

function formatDate(dateStr: string) {
  try {
    return format(parseISO(dateStr), 'MMM d');
  } catch {
    return dateStr;
  }
}

function getRestingHrStatus(hr: number): { label: string; color: string } {
  if (hr < 60) return { label: 'Athletic', color: 'text-success' };
  if (hr < 70) return { label: 'Good', color: 'text-info' };
  if (hr < 80) return { label: 'Average', color: 'text-warning' };
  return { label: 'Above Average', color: 'text-warning' };
}

function calculateTrend(data: HeartRateTrendPoint[]): 'improving' | 'declining' | 'stable' {
  if (data.length < 7) return 'stable';

  const midpoint = Math.floor(data.length / 2);
  const firstHalf = data.slice(0, midpoint);
  const secondHalf = data.slice(midpoint);

  const firstAvg = firstHalf.reduce((sum, d) => sum + (d.resting || 0), 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((sum, d) => sum + (d.resting || 0), 0) / secondHalf.length;

  // Lower resting HR is better, so declining average = improving
  if (secondAvg < firstAvg * 0.95) return 'improving';
  if (secondAvg > firstAvg * 1.05) return 'declining';
  return 'stable';
}

export function HeartRateTrendsChart({ clientId, days = 30, className }: HeartRateTrendsChartProps) {
  const { data, loading, error } = useHeartRateTrends(clientId, days);

  if (loading) {
    return (
      <Card className={cn('border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-32" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="chart-height-sm w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <Card className={cn('border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Heart className="h-5 w-5 text-chart-8" />
            Heart Rate Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center chart-height-sm text-muted-foreground">
            <Heart className="h-12 w-12 mb-2 opacity-20" />
            <p className="text-sm">No heart rate data available</p>
            <p className="text-xs">Connect a health device to track heart rate</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const trend = calculateTrend(data);
  const avgResting = data.reduce((sum, d) => sum + (d.resting || 0), 0) / data.length;
  const latestResting = data[data.length - 1]?.resting || 0;
  const hrStatus = getRestingHrStatus(latestResting);

  // Transform data for chart
  const chartData = data.map((d) => ({
    date: formatDate(d.date),
    resting: d.resting,
    average: d.average,
    max: d.max,
    min: d.min,
  }));

  // Calculate domain with padding
  const allValues = data.flatMap((d) => [d.resting, d.max, d.min].filter(Boolean) as number[]);
  const minHr = Math.min(...allValues);
  const maxHr = Math.max(...allValues);
  const padding = (maxHr - minHr) * 0.1;

  return (
    <Card className={cn('border', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-chart-8" />
            Heart Rate Trends
          </span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              {trend === 'improving' && <TrendingDown className="h-4 w-4 text-success" />}
              {trend === 'declining' && <TrendingUp className="h-4 w-4 text-destructive" />}
              {trend === 'stable' && <Minus className="h-4 w-4 text-muted-foreground" />}
              <span className="capitalize">{trend}</span>
            </div>
            <div className="text-right">
              <span className={cn('text-xl font-bold', hrStatus.color)}>
                {latestResting} bpm
              </span>
              <span className={cn('text-xs block', hrStatus.color)}>
                Resting: {hrStatus.label}
              </span>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div role="img" aria-label="Heart rate trends over time">
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              domain={[
                Math.max(40, Math.floor(minHr - padding)),
                Math.ceil(maxHr + padding),
              ]}
              tickFormatter={(value) => `${value}`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              formatter={(value: number, name: string) => {
                const labels: Record<string, string> = {
                  resting: 'Resting HR',
                  average: 'Avg HR',
                  max: 'Max HR',
                  min: 'Min HR',
                };
                return [`${value} bpm`, labels[name] || name];
              }}
            />
            <ReferenceLine
              y={avgResting}
              stroke="#94a3b8"
              strokeDasharray="5 5"
              label={{ value: 'Avg Resting', fontSize: 10, fill: '#94a3b8' }}
            />
            {/* Range area between min and max */}
            <Area
              type="monotone"
              dataKey="max"
              stackId="1"
              stroke="transparent"
              fill="#fecaca"
              fillOpacity={0.3}
            />
            <Area
              type="monotone"
              dataKey="average"
              stackId="2"
              stroke="#f97316"
              strokeWidth={1}
              strokeDasharray="3 3"
              fill="transparent"
            />
            {/* Resting HR line */}
            <Line
              type="monotone"
              dataKey="resting"
              stroke="#e11d48"
              strokeWidth={2}
              dot={{ fill: '#e11d48', strokeWidth: 0, r: 3 }}
              activeDot={{ r: 5, fill: '#e11d48' }}
            />
          </AreaChart>
        </ResponsiveContainer>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-4 gap-2 mt-4 text-center text-xs">
          <div className="bg-chart-8/10 rounded p-2">
            <div className="text-muted-foreground">Resting</div>
            <div className={cn('font-semibold', hrStatus.color)}>{latestResting} bpm</div>
          </div>
          <div className="bg-muted/50 rounded p-2">
            <div className="text-muted-foreground">Avg Resting</div>
            <div className="font-semibold">{Math.round(avgResting)} bpm</div>
          </div>
          <div className="bg-destructive/10 rounded p-2">
            <div className="text-muted-foreground">Max</div>
            <div className="font-semibold text-destructive">
              {Math.max(...data.map((d) => d.max || 0))} bpm
            </div>
          </div>
          <div className="bg-info/10 rounded p-2">
            <div className="text-muted-foreground">Min</div>
            <div className="font-semibold text-info">
              {Math.min(...data.filter((d) => d.min).map((d) => d.min || 999))} bpm
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
